import {
  LevelType,
  PositionAndDimensionsType,
  SpacePositionData
} from 'data/types';
import { findSpaceById } from 'utils/structure';

export type SectionLabelGroup<T extends SpacePositionData> = {
  key: string;
  sectionId: string;
  label: string;
  spaces: T[];
  largestSpace: T;
  bounds: PositionAndDimensionsType;
};

type LabelSize = {
  width: number;
  height: number;
};

type CenteredLabelOptions = {
  padding?: number;
};

function getGroupKey(sectionId: string, label: string): string {
  return `${sectionId}::${label}`;
}

function getLargestSpace<T extends SpacePositionData>(spaces: T[]): T {
  const sortedSpaces = [...spaces].sort((a, b) => {
    const areaDelta =
      b.position.width * b.position.height -
      a.position.width * a.position.height;
    if (areaDelta !== 0) {
      return areaDelta;
    }
    if (a.position.y !== b.position.y) {
      return a.position.y - b.position.y;
    }
    return a.position.x - b.position.x;
  });
  return sortedSpaces[0];
}

function getBounds<T extends SpacePositionData>(
  spaces: T[]
): PositionAndDimensionsType {
  const minX = Math.min(...spaces.map((space) => space.position.x));
  const minY = Math.min(...spaces.map((space) => space.position.y));
  const maxX = Math.max(
    ...spaces.map((space) => space.position.x + space.position.width)
  );
  const maxY = Math.max(
    ...spaces.map((space) => space.position.y + space.position.height)
  );

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY
  };
}

export function getSectionLabelGroups<T extends SpacePositionData>(
  allSpaces: T[],
  structure: LevelType[]
): Map<string, SectionLabelGroup<T>> {
  const groupsMap = new Map<string, T[]>();

  allSpaces.forEach((space) => {
    if (!space.sectionId || (space.spacesInSection ?? 1) <= 1) {
      return;
    }

    const structureSpace = findSpaceById(structure, space.spaceId);
    if ((structureSpace?.openTo?.length ?? 0) === 0) {
      return;
    }

    const key = getGroupKey(space.sectionId, space.label);
    const current = groupsMap.get(key) ?? [];
    groupsMap.set(key, [...current, space]);
  });

  const sectionLabelGroups = new Map<string, SectionLabelGroup<T>>();

  groupsMap.forEach((spaces, key) => {
    if (spaces.length <= 1) {
      return;
    }

    const largestSpace = getLargestSpace(spaces);
    const bounds = getBounds(spaces);
    sectionLabelGroups.set(key, {
      key,
      sectionId: largestSpace.sectionId!,
      label: largestSpace.label,
      spaces,
      largestSpace,
      bounds
    });
  });

  return sectionLabelGroups;
}

export function getSectionLabelGroupForSpace<T extends SpacePositionData>(
  space: T,
  groups: Map<string, SectionLabelGroup<T>>
): SectionLabelGroup<T> | null {
  if (!space.sectionId) {
    return null;
  }
  return groups.get(getGroupKey(space.sectionId, space.label)) ?? null;
}

export function shouldShowLargestLabelForGroupedSpace<
  T extends SpacePositionData
>(space: T, allSpaces: T[], structure: LevelType[]): boolean {
  const groups = getSectionLabelGroups(allSpaces, structure);
  const group = getSectionLabelGroupForSpace(space, groups);
  if (!group) {
    return true;
  }
  return group.largestSpace.id === space.id;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function getNearestPointInsideSpaces<T extends SpacePositionData>(
  spaces: T[],
  desiredX: number,
  desiredY: number,
  padding: number
): { x: number; y: number } {
  let closestPoint = {
    x: spaces[0].position.x + spaces[0].position.width / 2,
    y: spaces[0].position.y + spaces[0].position.height / 2
  };
  let minDistanceSquared = Number.POSITIVE_INFINITY;

  spaces.forEach((space) => {
    const left = space.position.x + padding;
    const right = space.position.x + space.position.width - padding;
    const top = space.position.y + padding;
    const bottom = space.position.y + space.position.height - padding;
    if (left > right || top > bottom) {
      return;
    }
    const clampedX = clamp(desiredX, left, right);
    const clampedY = clamp(desiredY, top, bottom);
    const distanceSquared =
      (clampedX - desiredX) * (clampedX - desiredX) +
      (clampedY - desiredY) * (clampedY - desiredY);

    if (distanceSquared < minDistanceSquared) {
      minDistanceSquared = distanceSquared;
      closestPoint = { x: clampedX, y: clampedY };
    }
  });

  return closestPoint;
}

function computeSegmentCoveredHeight<T extends SpacePositionData>(
  spaces: T[],
  sampleX: number,
  top: number,
  bottom: number
): number {
  const yIntervals: Array<{ start: number; end: number }> = [];

  spaces.forEach((space) => {
    const spaceLeft = space.position.x;
    const spaceRight = space.position.x + space.position.width;
    if (sampleX < spaceLeft || sampleX > spaceRight) {
      return;
    }
    const intervalStart = Math.max(top, space.position.y);
    const intervalEnd = Math.min(
      bottom,
      space.position.y + space.position.height
    );
    if (intervalStart < intervalEnd) {
      yIntervals.push({ start: intervalStart, end: intervalEnd });
    }
  });

  if (yIntervals.length === 0) {
    return 0;
  }

  yIntervals.sort((a, b) => a.start - b.start);
  let mergedStart = yIntervals[0].start;
  let mergedEnd = yIntervals[0].end;
  let coveredHeight = 0;

  for (let index = 1; index < yIntervals.length; index += 1) {
    const interval = yIntervals[index];
    if (interval.start <= mergedEnd) {
      mergedEnd = Math.max(mergedEnd, interval.end);
    } else {
      coveredHeight += mergedEnd - mergedStart;
      mergedStart = interval.start;
      mergedEnd = interval.end;
    }
  }
  coveredHeight += mergedEnd - mergedStart;

  return coveredHeight;
}

function getUnionOverlapArea<T extends SpacePositionData>(
  spaces: T[],
  left: number,
  right: number,
  top: number,
  bottom: number
): number {
  if (left >= right || top >= bottom) {
    return 0;
  }

  const xBreakpoints = new Set<number>([left, right]);
  spaces.forEach((space) => {
    const spaceLeft = space.position.x;
    const spaceRight = space.position.x + space.position.width;
    const overlapLeft = Math.max(left, spaceLeft);
    const overlapRight = Math.min(right, spaceRight);
    if (overlapLeft < overlapRight) {
      xBreakpoints.add(overlapLeft);
      xBreakpoints.add(overlapRight);
    }
  });

  const sortedX = Array.from(xBreakpoints).sort((a, b) => a - b);
  let totalArea = 0;

  for (let i = 0; i < sortedX.length - 1; i += 1) {
    const segmentLeft = sortedX[i];
    const segmentRight = sortedX[i + 1];
    const segmentWidth = segmentRight - segmentLeft;

    if (segmentWidth > 0) {
      const sampleX = (segmentLeft + segmentRight) / 2;
      const coveredHeight = computeSegmentCoveredHeight(
        spaces,
        sampleX,
        top,
        bottom
      );
      totalArea += coveredHeight * segmentWidth;
    }
  }

  return totalArea;
}

export function getLargestLabelPosition<T extends SpacePositionData>(group: {
  largestSpace: T;
}): { x: number; y: number } {
  return {
    x: group.largestSpace.position.x + group.largestSpace.position.width / 2,
    y: group.largestSpace.position.y + group.largestSpace.position.height / 2
  };
}

export function getCenteredLabelPosition<T extends SpacePositionData>(
  group: {
    bounds: PositionAndDimensionsType;
    spaces: T[];
    largestSpace: T;
  },
  labelSize?: LabelSize,
  options?: CenteredLabelOptions
): { x: number; y: number } {
  const desiredX = group.bounds.x + group.bounds.width / 2;
  const desiredY = group.bounds.y + group.bounds.height / 2;
  const padding = Math.max(0, options?.padding ?? 0);
  const halfWidth = Math.max(0, (labelSize?.width ?? 0) / 2);
  const halfHeight = Math.max(0, (labelSize?.height ?? 0) / 2);

  if (halfWidth === 0 && halfHeight === 0) {
    return getNearestPointInsideSpaces(
      group.spaces,
      desiredX,
      desiredY,
      padding
    );
  }

  const candidateXs = new Set<number>([desiredX]);
  const candidateYs = new Set<number>([desiredY]);

  group.spaces.forEach((space) => {
    const left = space.position.x + halfWidth + padding;
    const right = space.position.x + space.position.width - halfWidth - padding;
    const top = space.position.y + halfHeight + padding;
    const bottom =
      space.position.y + space.position.height - halfHeight - padding;

    if (left <= right) {
      candidateXs.add(left);
      candidateXs.add(right);
      candidateXs.add(clamp(desiredX, left, right));
    }

    if (top <= bottom) {
      candidateYs.add(top);
      candidateYs.add(bottom);
      candidateYs.add(clamp(desiredY, top, bottom));
    }
  });

  const validCandidates: Array<{
    x: number;
    y: number;
    distanceSquared: number;
  }> = [];
  const overlapCandidates: Array<{
    x: number;
    y: number;
    distanceSquared: number;
    overlapArea: number;
  }> = [];
  const labelArea = halfWidth * 2 * (halfHeight * 2);

  Array.from(candidateXs).forEach((x) => {
    Array.from(candidateYs).forEach((y) => {
      const left = x - halfWidth;
      const right = x + halfWidth;
      const top = y - halfHeight;
      const bottom = y + halfHeight;
      const overlapArea = getUnionOverlapArea(
        group.spaces,
        left,
        right,
        top,
        bottom
      );
      const distanceSquared =
        (x - desiredX) * (x - desiredX) + (y - desiredY) * (y - desiredY);

      if (Math.abs(overlapArea - labelArea) < 0.5) {
        validCandidates.push({ x, y, distanceSquared });
      } else {
        overlapCandidates.push({ x, y, distanceSquared, overlapArea });
      }
    });
  });

  if (validCandidates.length > 0) {
    validCandidates.sort((a, b) => a.distanceSquared - b.distanceSquared);
    return { x: validCandidates[0].x, y: validCandidates[0].y };
  }

  if (overlapCandidates.length > 0) {
    overlapCandidates.sort((a, b) => {
      if (b.overlapArea !== a.overlapArea) {
        return b.overlapArea - a.overlapArea;
      }
      return a.distanceSquared - b.distanceSquared;
    });
    return { x: overlapCandidates[0].x, y: overlapCandidates[0].y };
  }

  return getLargestLabelPosition(group);
}
